import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import { journeyApi } from '../../api/journeyApi';
import { journeyNoticeApi } from '../../api/journeyNoticeApi';
import { useAuth } from '../../auth/AuthContext';
import { WORKSPACE_PRODUCT_CHROME } from '../journey-workspace/journeyWorkspacePresentation';
import {
  classifyJourneyAccessError,
  normalizeJourneyWorkspace,
} from '../journey-workspace/journeyWorkspaceModel';
import JourneyWorkspaceNav from '../journey-workspace/JourneyWorkspaceNav';
import { useJourneyNoticeUnreadCount } from './useJourneyNoticeUnreadCount';
import {
  buildNoticeMutationBody,
  classifyNoticeError,
  mergeNotice,
  normalizeNoticePage,
  noticeErrorMessage,
  unwrapNoticeCommand,
} from './journeyNoticeModel';
import '../journey-workspace/journey-workspace.css';
import './journey-notice.css';

const PAGE_LIMIT = 20;

function NoticeItem({
  notice,
  pendingKey,
  onMarkRead,
  onAcknowledge,
}) {
  const busy = Boolean(pendingKey);
  return (
    <li>
      <article
        className={`journey-notice__item ${notice.unread ? 'is-unread' : 'is-read'}`}
        aria-labelledby={`notice-title-${notice.noticeId}`}
      >
        <p className="journey-notice__meta">
          <span className="journey-notice__status">{notice.statusLabel}</span>
          {' · '}
          <span>{notice.typeLabel}</span>
          {notice.sourceLabel ? ` · ${notice.sourceLabel}` : ''}
          {notice.ackRequired ? ' · 需要確認收到' : ''}
        </p>
        <h3 className="journey-notice__title" id={`notice-title-${notice.noticeId}`}>{notice.title}</h3>
        {notice.body ? <p className="journey-notice__body">{notice.body}</p> : null}
        <p className="journey-notice__source">
          {notice.createTime ? (
            <time dateTime={notice.createTime}>{formatDateTime(notice.createTime)}</time>
          ) : null}
          {notice.severity ? ` · ${notice.severity}` : ''}
        </p>
        <div className="journey-notice__actions">
          {notice.unread ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => onMarkRead(notice)}
            >
              {pendingKey === 'read' ? '標記中…' : '標為已讀'}
            </button>
          ) : null}
          {notice.canAcknowledge ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => onAcknowledge(notice)}
            >
              {pendingKey === 'ack' ? '確認中…' : '確認收到'}
            </button>
          ) : null}
          {notice.sourceRoute ? (
            <Link to={notice.sourceRoute} onClick={() => { if (notice.unread) onMarkRead(notice); }}>
              {notice.ctaLabel}
            </Link>
          ) : null}
        </div>
      </article>
    </li>
  );
}

export default function JourneyNoticesPage() {
  const { journeyId } = useParams();
  const { user } = useAuth();
  const viewerMemberId = user?.memberId ?? user?.id;
  const generation = useRef(0);
  const mutating = useRef('');
  const { unreadCount, refresh: refreshUnread } = useJourneyNoticeUnreadCount(journeyId);

  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionStatus, setActionStatus] = useState('');
  const [journey, setJourney] = useState(null);
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pending, setPending] = useState({ noticeId: '', action: '' });

  const load = useCallback(async ({ soft = false } = {}) => {
    const requestId = ++generation.current;
    if (!soft) {
      setPhase('loading');
      setError('');
      setActionError('');
    }
    try {
      const [detailPayload, listPayload] = await Promise.all([
        journeyApi.getDetail(journeyId),
        journeyNoticeApi.list(journeyId, { limit: PAGE_LIMIT }),
      ]);
      if (requestId !== generation.current) return;
      const page = normalizeNoticePage(listPayload);
      setJourney(normalizeJourneyWorkspace(detailPayload, viewerMemberId));
      setItems(page.items);
      setNextCursor(page.nextCursor);
      setHasMore(page.hasMore);
      setPhase('ready');
      refreshUnread();
    } catch (cause) {
      if (requestId !== generation.current) return;
      const kind = classifyNoticeError(cause);
      const access = classifyJourneyAccessError(cause);
      if (kind === 'journey-not-found' || access === 'not-found') setPhase('not-found');
      else if (kind === 'forbidden' || access === 'forbidden') setPhase('forbidden');
      else if (kind === 'unauthorized') setPhase('forbidden');
      else {
        setError(noticeErrorMessage(cause, '旅程通知無法載入。'));
        setPhase('error');
      }
    }
  }, [journeyId, viewerMemberId, refreshUnread]);

  useEffect(() => {
    if (!/^[1-9]\d*$/.test(journeyId || '')) {
      setPhase('not-found');
      return undefined;
    }
    load();
    return () => { generation.current += 1; };
  }, [journeyId, load]);

  async function loadMore() {
    if (loadingMore || !hasMore || !nextCursor) return;
    setLoadingMore(true);
    setActionError('');
    try {
      const payload = await journeyNoticeApi.list(journeyId, {
        limit: PAGE_LIMIT,
        cursorCreateTime: nextCursor.createTime,
        cursorNoticeId: nextCursor.noticeId,
      });
      const page = normalizeNoticePage(payload);
      setItems((current) => {
        const seen = new Set(current.map((item) => item.noticeId));
        return [...current, ...page.items.filter((item) => !seen.has(item.noticeId))];
      });
      setNextCursor(page.nextCursor);
      setHasMore(page.hasMore);
    } catch (cause) {
      setActionError(noticeErrorMessage(cause, '無法載入更多通知。'));
    } finally {
      setLoadingMore(false);
    }
  }

  async function runMutation(notice, action) {
    const lock = `${notice.noticeId}:${action}`;
    if (mutating.current) return;
    mutating.current = lock;
    setPending({ noticeId: notice.noticeId, action });
    setActionError('');
    setActionStatus('');
    try {
      const body = buildNoticeMutationBody(notice);
      const payload = action === 'ack'
        ? await journeyNoticeApi.acknowledge(journeyId, notice.noticeId, body)
        : await journeyNoticeApi.markRead(journeyId, notice.noticeId, body);
      const updated = unwrapNoticeCommand(payload).notice;
      setItems((current) => mergeNotice(current, updated));
      setActionStatus(action === 'ack' ? '已確認收到。' : '已標為已讀。');
      refreshUnread();
    } catch (cause) {
      const kind = classifyNoticeError(cause);
      setActionError(noticeErrorMessage(cause, '通知操作失敗。'));
      if (kind === 'conflict' || kind === 'not-found') {
        load({ soft: true });
      }
    } finally {
      mutating.current = '';
      setPending({ noticeId: '', action: '' });
    }
  }

  if (phase === 'loading') {
    return <main className="journey-workspace-state" role="status">載入旅程通知…</main>;
  }
  if (phase === 'not-found') {
    return (
      <main className="journey-workspace-state">
        <h1>找不到這趟旅程</h1>
        <Link to="/journeys">{WORKSPACE_PRODUCT_CHROME.myJourneys}</Link>
      </main>
    );
  }
  if (phase === 'forbidden') {
    return (
      <main className="journey-workspace-state">
        <h1>沒有權限查看旅程通知</h1>
        <p>只有進行中的旅程成員可以讀取自己的通知。前端隱藏不是權限依據。</p>
        <Link to="/journeys">{WORKSPACE_PRODUCT_CHROME.myJourneys}</Link>
      </main>
    );
  }
  if (phase === 'error') {
    return (
      <main className="journey-workspace-state">
        <h1>旅程通知無法載入</h1>
        <p role="alert">{error}</p>
        <button type="button" onClick={() => load()}>重試</button>
      </main>
    );
  }

  const unreadLabel = unreadCount == null ? '' : `${unreadCount} 則未讀`;

  return (
    <main className="journey-workspace journey-notice">
      <header className="journey-workspace__header">
        <div>
          <p className="journey-workspace__eyebrow">旅程通知</p>
          <h1>旅程通知</h1>
          <p className="journey-workspace__meta">
            {journey?.title || '未命名行程'}
            {unreadLabel ? ` · ${unreadLabel}` : ''}
          </p>
        </div>
        <Link className="journey-workspace__back" to={`/journeys/${journey.journeyId}`}>{WORKSPACE_PRODUCT_CHROME.overview}</Link>
      </header>

      <JourneyWorkspaceNav journeyId={journey.journeyId} unreadCount={unreadCount} />

      <div aria-live="polite">
        {actionStatus ? <p role="status">{actionStatus}</p> : null}
        {actionError ? <p role="alert">{actionError}</p> : null}
      </div>

      {items.length === 0 ? (
        <p className="journey-workspace__hint" role="status">目前沒有旅程通知</p>
      ) : (
        <ul className="journey-notice__list">
          {items.map((notice) => (
            <NoticeItem
              key={notice.noticeId}
              notice={notice}
              pendingKey={pending.noticeId === notice.noticeId ? pending.action : ''}
              onMarkRead={(item) => runMutation(item, 'read')}
              onAcknowledge={(item) => runMutation(item, 'ack')}
            />
          ))}
        </ul>
      )}

      {hasMore ? (
        <p>
          <button
            type="button"
            className="journey-notice__load-more"
            disabled={loadingMore}
            onClick={loadMore}
          >
            {loadingMore ? '載入中…' : '載入更多通知'}
          </button>
        </p>
      ) : null}
    </main>
  );
}
